import { createFlow, type Flow } from '@adv-frontend/modules-flow';
import { memoize } from '@adv-frontend/modules-memo';
import type { ApiEndpointMutation, MutationResultSelectorResult } from '../rtk-query-types';
import type { AnyApi } from '../buildCreateApi';
import type { GetStoreFn } from '../types';
import { createListenersCollection } from '../createListenersCollection';
import { createStoreListener } from './buildEndpointQueryFlow';
import type { AnyMutationDefinition, AnyEndpointMutation } from './types';

/**
 * Набор дополнительных методов эндпоинта, которые добавляются в RTK Query.
 */
export type EndpointMutationState = {
    /**
     * Метод для получения потока с состоянием мутации.
     * @param fixedCacheKey - Ключ мутации, который был передан в метод mutate()
     * @returns Поток с состоянием мутации
     */
    mutationState: (fixedCacheKey: string) => MutationStateFlow;
};

/**
 * Информация о текущем выполнении мутации
 */
interface MutationState {
    /**
     * Состояние загрузки.
     * Всегда true, потому что жизненный цикл мутации ограничен выполнением запроса.
     * При завершении мутации поток начинает отдавать null.
     */
    isLoading: true;
    /**
     * Уникальный идентификатор запроса мутации.
     */
    requestId: string;
}

/**
 * Тип потока для отслеживания состояния мутации
 * Возвращает либо объект состояния мутации, либо null при завершении мутации
 */
type MutationStateFlow = Flow<null | MutationState>;

/**
 * Параметры для `buildEndpointMutationState()`.
 */
type BuildMutationStateOptions = {
    /** API экземпляр с эндпоинтами */
    api: AnyApi;
    /** Имя эндпоинта мутации */
    endpointName: string;
    /** Функция для получения store */
    getStore: GetStoreFn;
};

export function buildEndpointMutationState({
    api,
    endpointName,
    getStore,
}: BuildMutationStateOptions): EndpointMutationState['mutationState'] {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    const endpoint = api.endpoints[endpointName] as AnyEndpointMutation;

    return memoize((fixedCacheKey: string) => {
        return createMutationStateFlow({
            endpoint,
            getStore,
            fixedCacheKey,
        });
    });
}

/**
 * Опции для создания потока состояния мутации.
 *
 * @typeParam Definition - Описание эндпоинта мутации
 */
interface CreateMutationStateFlowOptions<Definition extends AnyMutationDefinition> {
    /** Эндпоинт мутации */
    endpoint: ApiEndpointMutation<Definition, {}>;
    /** Функция для получения store */
    getStore: GetStoreFn;
    /** Ключ кеша мутации */
    fixedCacheKey: string;
}

/**
 * Создает поток для отслеживания состояния мутации.
 *
 * @typeParam Definition - Описание эндпоинта мутации
 * @param options - Опции для создания потока
 * @returns Объект с потоком состояния мутации
 */
function createMutationStateFlow<Definition extends AnyMutationDefinition>({
    endpoint,
    getStore,
    fixedCacheKey,
}: CreateMutationStateFlowOptions<Definition>): MutationStateFlow {
    const dataSelector = endpoint.select({ requestId: undefined, fixedCacheKey });
    const getState = () => dataSelector(getStore().getState());

    const flow = createFlow(mapMutationState(getState()));

    const storeListener = createStoreListener({
        getStore,
        getState,
        equals(a, b) {
            return a.status === b.status && a.isLoading === b.isLoading && a.requestId === b.requestId;
        },
        onChange(state) {
            try {
                flow.emit(mapMutationState(state));
            } catch (err) {
                // Метод emit() проксирует ошибки от слушателей потока.
                // Не хотим, чтобы ошибки слушателей потока влияли на работу самого потока,
                // поэтому ловим ошибки и просто их логируем
                console.error(err);
            }
        },
    });

    const listeners = createListenersCollection({
        onAdd(listener) {
            return flow.subscribe(listener);
        },
        onActivate() {
            storeListener.subscribe();

            // Пока слушателей не было, мутация могла завершиться. Синхронизируем поток с текущим
            // состоянием стора, иначе getSnapshot() с подписчиками (который syncState не вызывает)
            // отдавал бы сохраненный pending.
            storeListener.syncState();

            return () => storeListener.unsubscribe();
        },
    });

    return {
        subscribe(listener) {
            return listeners.add(listener);
        },
        getSnapshot() {
            if (!listeners.hasListeners()) {
                storeListener.syncState();
            }

            return flow.getSnapshot();
        },
        getCachedSnapshot() {
            return flow.getCachedSnapshot();
        },
    };
}

/**
 * Преобразует результат селектора мутации в состояние мутации
 *
 * @typeParam Definition - Тип определения мутации
 * @param state - Результат селектора мутации
 * @returns Объект состояния мутации или null, если мутация не в ожидании
 */
function mapMutationState<Definition extends AnyMutationDefinition>(
    state: MutationResultSelectorResult<Definition>
): MutationState | null {
    if (state.status !== 'pending') {
        return null;
    }

    return {
        isLoading: state.isLoading,
        requestId: state.requestId,
    };
}
